import { useLemonadeContext } from "@/LemonadeProvider";
import { Line } from "react-chartjs-2";
import { Chart, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend } from "chart.js";
import { useMemo } from "react";
import "../styles/DailyStatsChart.css";

Chart.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend);

function formatDate(date: Date) {
    return date.toISOString().slice(0, 10);
}

// helper to get CSS variable value
function cssVar(name: string, fallback: string) {
    if (typeof window === "undefined") return fallback;
    return getComputedStyle(document.documentElement).getPropertyValue(name) || fallback;
}

export function DailyStatsChart() {
    const { sales, purchases } = useLemonadeContext();

    // get theme colors
    const primary = cssVar("--primary", "#efd58e").trim();
    const accent = cssVar("--accent", "#7ea57e").trim();
    const success = cssVar("--success", "#339a2c").trim();
    const error = cssVar("--error", "#ee6868").trim();
    const text = cssVar("--text", "#faf3dd").trim();
    const background = cssVar("--background", "#1d1604").trim();

    // aggregate daily income, expenses, and profit
    const { labels, income, expenses, profit } = useMemo(() => {
        const dayMap: Record<string, { income: number; expenses: number }> = {};

        for (const sale of sales) {
            const day = formatDate(new Date(sale.date));
            if (!dayMap[day]) dayMap[day] = { income: 0, expenses: 0 };
            dayMap[day].income += sale.price;
        }
        for (const purchase of purchases) {
            const day = formatDate(new Date(purchase.date));
            if (!dayMap[day]) dayMap[day] = { income: 0, expenses: 0 };
            dayMap[day].expenses += purchase.price;
        }

        const sortedDays = Object.keys(dayMap).sort((a, b) => a.localeCompare(b));
        return {
            labels: sortedDays,
            income: sortedDays.map((d) => dayMap[d].income),
            expenses: sortedDays.map((d) => dayMap[d].expenses),
            profit: sortedDays.map((d) => dayMap[d].income - dayMap[d].expenses)
        };
    }, [sales, purchases]);

    const data = {
        labels,
        datasets: [
            {
                label: "Bevétel",
                data: income,
                borderColor: success,
                backgroundColor: success,
                fill: false,
                tension: 0.3,
                pointRadius: 3,
                pointBackgroundColor: success
            },
            {
                label: "Kiadás",
                data: expenses,
                borderColor: error,
                backgroundColor: error,
                fill: false,
                tension: 0.3,
                pointRadius: 3,
                pointBackgroundColor: error
            },
            {
                label: "Nyereség",
                data: profit,
                borderColor: primary,
                backgroundColor: primary,
                fill: false,
                tension: 0.3,
                pointRadius: 3,
                pointBackgroundColor: primary
            }
        ]
    };

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: "top" as const,
                labels: {
                    color: text
                }
            },
            tooltip: {
                mode: "index" as const,
                intersect: false,
                backgroundColor: background,
                titleColor: primary,
                bodyColor: text,
                borderColor: accent,
                borderWidth: 1
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                title: { display: true, text: "Ft", color: text },
                ticks: { color: text },
                grid: { color: accent }
            },
            x: {
                title: { display: true, text: "Dátum", color: text },
                ticks: { color: text },
                grid: { color: accent }
            }
        }
    };

    return (
        <section className="chart-section" id="daily-stats-chart">
            <h1 className="main-title">Napi bevétel, kiadás és nyereség</h1>
            <Line data={data} options={options} />
        </section>
    );
}
